import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, CheckCircle2, Download, FileSpreadsheet, FileText, Loader2, Lock, MessageSquarePlus, Wrench, XCircle } from "lucide-react";

import { ApiError } from "@/api/client";
import type { Aclaracion, AnalysisMessage, PendingApproval, TokenUsage } from "@/api/types";
import { AROCIcon } from "@/components/AROCIcon";
import { ApprovalCard } from "@/components/ApprovalCard";
import { MarkdownLite } from "@/components/MarkdownLite";
import { Button } from "@/components/ui/button";
import { useAnalysisSession, useBudget, useChatSessions, useOpenChat } from "@/hooks/useAnalysis";
import { useAnalysisStream } from "@/hooks/useAnalysisStream";
import { usePendingApprovals } from "@/hooks/useApprovals";
import { useAuth } from "@/hooks/useAuth";
import { useIncidents } from "@/hooks/useIncidents";
import { useMyProfile } from "@/hooks/useMe";
import { useRecetasEstado } from "@/hooks/useRecetas";
import { useAuditoriaEstado } from "@/hooks/useAuditoriaPlaticas";
import { GuardarRecetaDialog } from "@/components/GuardarRecetaDialog";
import { useSelfUsage } from "@/hooks/useUsage";
import { cn } from "@/lib/utils";

/**
 * Hablando con AROC: plática libre con AROC sobre toda la red.
 *
 * Tres columnas, como el prototipo del 1-nov-2026:
 *   pláticas + "AROC ve ahora" | la plática | lo que AROC está revisando.
 * AROC sólo consulta con las herramientas que el permiso del usuario abre;
 * lo que proponga hacer pasa por Aprobaciones.
 */

// Qué herramienta mira qué, y qué segmento la abre (espejo de TOOL_SEGMENTS).
export const FUENTES: { tool: string; label: string; segment: string }[] = [
  { tool: "query_zabbix_problems", label: "Problemas activos en Zabbix", segment: "dashboard" },
  { tool: "query_zabbix_host_info", label: "Equipos en Zabbix", segment: "dashboard" },
  { tool: "query_zabbix_history", label: "Historial de Zabbix", segment: "dashboard" },
  { tool: "query_incidents", label: "Incidentes", segment: "dashboard" },
  { tool: "buscar_casos_cerrados", label: "Casos cerrados", segment: "dashboard" },
  { tool: "query_protocol_runs", label: "Corridas de remediación", segment: "runs" },
  { tool: "get_system_health", label: "Salud de AROC", segment: "health" },
  { tool: "consultar_agencias_holanda", label: "Agencias Holanda y SIM", segment: "dashboards" },
  { tool: "consultar_estacionamiento_holanda", label: "Estacionamiento Holanda", segment: "carritos" },
  { tool: "consultar_tickets_estacionamiento", label: "Tickets de estacionamiento", segment: "carritos" },
  { tool: "consultar_control_configuraciones", label: "Control de configuraciones", segment: "admin_config" },
  { tool: "consultar_zoom", label: "Zoom tiLatina", segment: "zoom" },
  { tool: "consultar_ecc", label: "ECC y contactabilidad", segment: "ecc" },
  { tool: "propose_protocol_execution", label: "Proponer remediaciones", segment: "approvals" },
  { tool: "propose_diagnostic", label: "Proponer diagnósticos", segment: "approvals" },
  { tool: "generar_documento", label: "Entregar en Word o Excel", segment: "analysis" },
];

const LABEL_POR_TOOL = new Map(FUENTES.map((f) => [f.tool, f.label]));

export function fuenteLabel(tool: string | undefined): string {
  if (!tool) return "herramienta";
  return LABEL_POR_TOOL.get(tool) ?? tool;
}

const SUGERENCIAS = [
  "¿Cómo amaneció Holanda?",
  "¿Qué agencias están en SIM?",
  "Resume los incidentes de ayer",
  "¿Cómo va el estacionamiento hoy?",
];

// El costo lo calcula el backend con el precio vigente de cada modelo.
export function motorLabel(provider?: string | null): string {
  if (provider === "gemini") return "Gemini 3.8 Flash";
  if (provider === "anthropic") return "Sonnet (respaldo)";
  return "—";
}

export function usd(n: number | undefined): string {
  return `US$${(n ?? 0).toFixed(4)}`;
}

export function primerDiaDelMes(hoy = new Date()): string {
  const m = String(hoy.getMonth() + 1).padStart(2, "0");
  return `${hoy.getFullYear()}-${m}-01`;
}

function hora(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return mismoDia
    ? d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
}

function errorTexto(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) return "Tu usuario no tiene permiso para hablar con AROC.";
  if (err instanceof ApiError && err.status === 429) return "AROC llegó a su cupo mensual. La plática queda guardada: síguela cuando suba el cupo o empiece el mes.";
  if (err instanceof ApiError && err.status === 503) return "El módulo de AROC no está configurado en este servidor.";
  return err instanceof Error ? err.message : "No se pudo abrir la plática.";
}

export function HablarConArocPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perfil = useMyProfile();
  const recetasEstado = useRecetasEstado();
  const auditoria = useAuditoriaEstado();
  // Guardar y programar: sólo administradores y con los reportes programados encendidos.
  const puedeReceta =
    recetasEstado.data?.enabled === true &&
    ((perfil.data?.global_roles ?? []).includes("super_user") || user?.role === "admin");

  const chats = useChatSessions();
  const openChat = useOpenChat();
  const sessionQuery = useAnalysisSession(sessionId);
  const stream = useAnalysisStream(sessionId);
  const approvals = usePendingApprovals();
  const [draft, setDraft] = useState("");
  const [bootError, setBootError] = useState<string | null>(null);

  // La primera pregunta abre la plática; se manda en cuanto existe la sesión.
  const pending = useRef<{ sessionId: string; text: string } | null>(null);
  const sendMessage = stream.sendMessage;
  useEffect(() => {
    const p = pending.current;
    if (!p || p.sessionId !== sessionId || !sessionQuery.data) return;
    pending.current = null;
    sendMessage(p.text);
  }, [sessionId, sessionQuery.data, sendMessage]);

  const session = sessionQuery.data;
  const isStreaming = stream.status === "streaming";
  const isClosed = session?.status === "completed" || session?.status === "cancelled";

  const ask = async (text: string) => {
    const q = text.trim();
    if (!q || isStreaming) return;
    setDraft("");
    setBootError(null);
    if (sessionId && !isClosed) {
      sendMessage(q);
      return;
    }
    try {
      const nueva = await openChat.mutateAsync();
      pending.current = { sessionId: nueva.id, text: q };
      navigate(`/hablar/${nueva.id}`);
    } catch (err) {
      setDraft(q);
      setBootError(errorTexto(err));
    }
  };

  const visibles = useMemo(
    () =>
      (session?.messages ?? []).filter(
        (m): m is AnalysisMessage =>
          m.role === "user" || m.role === "assistant" || documentoDe(m) !== null,
      ),
    [session?.messages],
  );

  const sessionApprovals = useMemo(
    () => (approvals.data ?? []).filter((a) => sessionId && a.analysis_session_id === sessionId),
    [approvals.data, sessionId],
  );

  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [visibles.length, stream.liveTokens, stream.liveToolCalls.length]);

  return (
    <div className="grid h-[calc(100vh-4rem)] grid-cols-1 gap-4 lg:grid-cols-[240px_1fr_280px]">
      <aside className="hidden flex-col gap-4 overflow-auto lg:flex">
        <Button onClick={() => navigate("/hablar")} className="w-full gap-2" variant="outline">
          <MessageSquarePlus className="size-4" /> Nueva plática
        </Button>
        <section>
          <h2 className="mb-1 px-1 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Pláticas
          </h2>
          <div className="space-y-1">
            {(chats.data?.items ?? []).map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/hablar/${c.id}`)}
                className={cn(
                  "w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted",
                  c.id === sessionId && "bg-muted",
                )}
              >
                <div className="truncate">{c.title ?? "Plática sin preguntas"}</div>
                <div className="text-xs text-muted-foreground">{hora(c.started_at)}</div>
              </button>
            ))}
            {chats.data && chats.data.items.length === 0 && (
              <div className="px-3 text-xs text-muted-foreground">Aún no hay pláticas.</div>
            )}
          </div>
        </section>
        <ArocVeAhora />
      </aside>

      <main className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card">
        <div ref={scroller} className="flex-1 space-y-4 overflow-auto p-4">
          {!sessionId && <Bienvenida onPick={ask} nombre={user?.username} />}
          {sessionId && !session && sessionQuery.isError && (
            <div className="text-sm text-muted-foreground">Esa plática no existe o no es tuya.</div>
          )}
          {visibles.map((m, i) => (
            <Burbuja
              key={`${m.timestamp}-${i}`}
              message={m}
              receta={puedeReceta && sessionId ? { sessionId } : undefined}
              // Sólo la última pregunta de AROC se contesta con un clic.
              onElegir={
                i === visibles.length - 1 && !isStreaming && !isClosed ? (o) => void ask(o) : undefined
              }
            />
          ))}
          {isStreaming && (
            <div className="flex gap-3">
              <AROCIcon size={28} />
              <div className="min-w-0 flex-1 rounded-lg border bg-background px-4 py-3">
                {stream.liveTokens ? (
                  <MarkdownLite text={stream.liveTokens} />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    {stream.liveToolCalls.length > 0
                      ? `Revisando ${fuenteLabel(stream.liveToolCalls[stream.liveToolCalls.length - 1]?.tool_name).toLowerCase()}…`
                      : "Pensando…"}
                  </div>
                )}
              </div>
            </div>
          )}
          {stream.status === "error" && (
            <div role="alert" className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {stream.errorMessage ?? "No pude completar la respuesta."}
            </div>
          )}
          {sessionApprovals.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                Propuestas en espera de aprobación
              </div>
              {sessionApprovals.map((a: PendingApproval) => (
                <ApprovalCard key={a.id} approval={a} />
              ))}
            </div>
          )}
        </div>
        <form
          className="border-t p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(draft);
          }}
        >
          {bootError && (
            <div role="alert" className="mb-2 text-sm text-destructive">
              {bootError}
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              aria-label="Pregunta para AROC"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void ask(draft);
                }
              }}
              rows={2}
              placeholder={isClosed ? "Plática cerrada: abre una nueva" : "Pregúntale a AROC…"}
              disabled={isStreaming || isClosed || openChat.isPending}
              className="min-h-[44px] flex-1 resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button type="submit" disabled={!draft.trim() || isStreaming || isClosed || openChat.isPending}>
              Enviar
            </Button>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            AROC sólo consulta. Toda acción que proponga pasa por Aprobaciones.
            {auditoria.data?.aviso && <> {auditoria.data.aviso}</>}
          </div>
        </form>
      </main>

      <aside className="hidden flex-col gap-4 overflow-auto lg:flex">
        <Revisando
          messages={session?.messages ?? []}
          live={stream.liveToolCalls}
          streaming={isStreaming}
        />
        <LoQueConoce />
        <Gasto usage={session?.token_usage} />
      </aside>
    </div>
  );
}

function Bienvenida({ onPick, nombre }: { onPick: (q: string) => void; nombre?: string }) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-12 text-center">
      <AROCIcon size={96} />
      <div>
        <h1 className="text-xl font-semibold">Hablando con AROC</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {nombre ? `${nombre}, pregúntame` : "Pregúntame"} lo que quieras de la red: agencias,
          incidentes, estacionamiento, Zoom, ECC, configuraciones. Te digo de dónde sale cada dato.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {SUGERENCIAS.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="rounded-full border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface DocumentoGenerado {
  documento_id: string;
  nombre: string;
  formato: "docx" | "xlsx";
  bytes: number;
  folio: string;
  descarga: string;
}

/** El resultado de `generar_documento` (JSON) o null si el mensaje no es eso. */
export function documentoDe(m: AnalysisMessage): DocumentoGenerado | null {
  if (m.role !== "tool_result" || m.tool_name !== "generar_documento") return null;
  try {
    const d = JSON.parse(m.content) as Partial<DocumentoGenerado>;
    if (!d.documento_id || !d.descarga?.startsWith("/api/")) return null;
    return d as DocumentoGenerado;
  } catch {
    return null;
  }
}

function tamano(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function TarjetaDocumento({ doc, receta }: { doc: DocumentoGenerado; receta?: { sessionId: string } }) {
  const Icono = doc.formato === "xlsx" ? FileSpreadsheet : FileText;
  const [guardando, setGuardando] = useState(false);
  return (
    <div className="ml-10 flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
      <Icono className="h-8 w-8 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{doc.nombre}</div>
        <div className="text-xs text-muted-foreground">
          {doc.formato === "xlsx" ? "Excel" : "Word"} · {tamano(doc.bytes)} · Folio {doc.folio} · Confidencial
        </div>
      </div>
      <Button asChild size="sm">
        <a href={doc.descarga} download={doc.nombre}>
          <Download className="mr-1 h-4 w-4" aria-hidden />
          Descargar
        </a>
      </Button>
      {receta && (
        <Button size="sm" variant="outline" onClick={() => setGuardando(true)}>
          <CalendarClock className="mr-1 h-4 w-4" aria-hidden />
          Guardar y programar
        </Button>
      )}
      {guardando && receta && (
        <GuardarRecetaDialog
          sessionId={receta.sessionId}
          documentoId={doc.documento_id}
          tituloSugerido={doc.nombre.replace(/ \d{4}-\d{2}-\d{2}\.(docx|xlsx)$/, "")}
          onClose={() => setGuardando(false)}
        />
      )}
    </div>
  );
}

function Burbuja({
  message,
  receta,
  onElegir,
}: {
  message: AnalysisMessage;
  receta?: { sessionId: string };
  onElegir?: (opcion: string) => void;
}) {
  const doc = documentoDe(message);
  if (doc) return <TarjetaDocumento doc={doc} receta={receta} />;
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground">
          <div className="whitespace-pre-wrap">{message.content}</div>
          <div className="mt-1 text-xs opacity-70">{hora(message.timestamp)}</div>
        </div>
      </div>
    );
  }
  if (!message.content?.trim()) return null;
  return (
    <div className="flex gap-3">
      <AROCIcon size={28} />
      <div className="min-w-0 flex-1 rounded-lg border bg-background px-4 py-3">
        <div className="mb-1 text-xs text-muted-foreground">AROC · {hora(message.timestamp)}</div>
        {message.aclaracion ? (
          <OpcionesAclaracion aclaracion={message.aclaracion} onElegir={onElegir} />
        ) : (
          <MarkdownLite text={message.content} />
        )}
        {message.usage && (
          <div className="mt-2 text-[11px] text-muted-foreground">
            {(message.usage.input_tokens + message.usage.output_tokens).toLocaleString("es-MX")}{" "}
            tokens · {usd(message.usage.estimated_cost_usd)}
            {message.usage.cost_missing && " · costo incompleto"}
          </div>
        )}
      </div>
    </div>
  );
}

export function OpcionesAclaracion({
  aclaracion,
  onElegir,
}: {
  aclaracion: Aclaracion;
  onElegir?: (opcion: string) => void;
}) {
  return (
    <div>
      <p className="text-sm">{aclaracion.pregunta}</p>
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Opciones de AROC">
        {aclaracion.opciones.map((o) => (
          <Button
            key={o}
            type="button"
            size="sm"
            variant="outline"
            disabled={!onElegir}
            onClick={() => onElegir?.(o)}
          >
            {o}
          </Button>
        ))}
      </div>
      {onElegir && (
        <div className="mt-2 text-xs text-muted-foreground">O escríbeme otra cosa abajo.</div>
      )}
    </div>
  );
}

function Gasto({ usage }: { usage?: TokenUsage }) {
  const mes = useSelfUsage({ since: primerDiaDelMes() });
  const t = mes.data?.totals;
  return (
    <section className="space-y-1 rounded-lg border bg-card p-3 text-xs text-muted-foreground">
      {usage && (
        <>
          <div>
            Esta plática: {usage.total_tokens.toLocaleString("es-MX")} tokens
            {usage.thought_tokens ? ` (${usage.thought_tokens.toLocaleString("es-MX")} pensando)` : ""}
            {" · "}
            {usd(usage.estimated_cost_usd)}
          </div>
          {usage.cost_missing && <div>Costo incompleto: algún motor no tiene precio cargado.</div>}
          <div>Motor: {motorLabel(usage.provider)}</div>
        </>
      )}
      {t && (
        <div>
          Este mes (tu cuenta): {(t.input_tokens + t.output_tokens).toLocaleString("es-MX")} tokens ·{" "}
          {usd(t.estimated_cost_usd)}
        </div>
      )}
    </section>
  );
}

function ArocVeAhora() {
  const incidents = useIncidents({ status: "open", severities: [], page: 1, perPage: 1 });
  const approvals = usePendingApprovals();
  const budget = useBudget();
  const filas: { label: string; value: string }[] = [
    { label: "Incidentes abiertos", value: incidents.data ? String(incidents.data.total) : "—" },
    { label: "Aprobaciones pendientes", value: approvals.data ? String(approvals.data.length) : "—" },
  ];
  if (budget.data?.enabled) {
    filas.push({ label: "Cupo del mes usado", value: `${Math.round(budget.data.percentage_used)}%` });
  }
  return (
    <section>
      <h2 className="mb-1 px-1 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        AROC ve ahora
      </h2>
      <div className="space-y-1 rounded-lg border bg-card p-3 text-sm">
        {filas.map((f) => (
          <div key={f.label} className="flex justify-between gap-2">
            <span className="text-muted-foreground">{f.label}</span>
            <span className="font-mono">{f.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

interface Paso {
  key: string;
  tool: string;
  detalle: string;
  estado: "running" | "ok" | "error";
  ms: number | null;
}

// Pasos del último turno: en vivo mientras corre; al terminar, los guardados
// (el stream no limpia sus pasos al cerrar, y al cambiar de plática serían ajenos).
export function pasosDelTurno(
  messages: AnalysisMessage[],
  live: ReturnType<typeof useAnalysisStream>["liveToolCalls"],
  streaming: boolean,
): Paso[] {
  if (streaming) {
    return live.map((t) => ({
      key: t.tool_use_id || t.tool_name,
      tool: t.tool_name,
      detalle: t.tool_input_summary,
      estado: t.state === "running" ? "running" : t.ok === false ? "error" : "ok",
      ms: t.duration_ms,
    }));
  }
  let start = 0;
  messages.forEach((m, i) => {
    if (m.role === "user") start = i;
  });
  return messages
    .slice(start)
    .filter((m) => m.role === "tool_call")
    .map((m, i) => ({
      key: `${m.timestamp}-${i}`,
      tool: m.tool_name ?? "",
      detalle: m.tool_input ? JSON.stringify(m.tool_input) : "",
      estado: "ok" as const,
      ms: null,
    }));
}

function Revisando({
  messages,
  live,
  streaming,
}: {
  messages: AnalysisMessage[];
  live: ReturnType<typeof useAnalysisStream>["liveToolCalls"];
  streaming: boolean;
}) {
  const pasos = pasosDelTurno(messages, live, streaming);
  return (
    <section>
      <h2 className="mb-1 px-1 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        Lo que AROC está revisando
      </h2>
      <div className="space-y-2 rounded-lg border bg-card p-3 text-sm">
        {pasos.length === 0 && (
          <div className="text-xs text-muted-foreground">
            Aquí verás qué fuentes consulta AROC para contestarte.
          </div>
        )}
        {pasos.map((p) => (
          <div key={p.key} className="flex gap-2">
            {p.estado === "running" ? (
              <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground" />
            ) : p.estado === "error" ? (
              <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
            ) : (
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
            )}
            <div className="min-w-0">
              <div>{fuenteLabel(p.tool)}</div>
              <div className="truncate font-mono text-[11px] text-muted-foreground" title={p.detalle}>
                {p.detalle}
                {p.ms != null && ` · ${(p.ms / 1000).toFixed(1)} s`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LoQueConoce() {
  const { user } = useAuth();
  const profile = useMyProfile();
  const segments = new Set(profile.data?.segments ?? []);
  const isSuper = (profile.data?.global_roles ?? []).includes("super_user") || user?.role === "admin";
  const puede = (s: string) => isSuper || segments.has(s);
  return (
    <section>
      <h2 className="mb-1 px-1 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        Lo que AROC conoce
      </h2>
      <div className="space-y-1 rounded-lg border bg-card p-3 text-xs">
        {FUENTES.map((f) => (
          <div
            key={f.tool}
            className={cn("flex items-center gap-2", !puede(f.segment) && "text-muted-foreground/60")}
          >
            {puede(f.segment) ? (
              <Wrench className="size-3 shrink-0 text-muted-foreground" />
            ) : (
              <Lock className="size-3 shrink-0" />
            )}
            {f.label}
          </div>
        ))}
        {!isSuper && (
          <div className="pt-1 text-[11px] text-muted-foreground">
            Lo que tiene candado no lo ve tu usuario; AROC tampoco te lo contesta.
          </div>
        )}
      </div>
    </section>
  );
}
